import { useCallback, useEffect, useMemo, useState } from 'react';

import { api, type JaratNap, type NapAdat, type NapokValasz, type Sofor } from '../api.ts';
import { useAlapadatok } from '../alapadatok-context.ts';
import { ft, szam } from '../formazas.ts';

/**
 * Nyomtatási kép — az eredeti papír sablon pontos mása. (Megbízás 6.2)
 *
 * Álló A4, oldalanként legfeljebb 41 sor. Az oszlopszélességek a megbízásban
 * megadott százalékok; ezeket ne igazgassuk „szebbre", mert az iroda éveken
 * át ehhez szokott, és a sofőrök ezen a lapon tájékozódnak.
 *
 * A lap **kétszer** jelenik meg a kódban: egyszer a képernyős előnézetnek
 * (`#pv` / `.ppage`), egyszer a tényleges nyomtatásnak (`#printarea` /
 * `.psheet`). Ez a demó felépítése — a két helyre külön méretezés vonatkozik,
 * mert a képernyőn nagyobb betű kell, mint a papíron.
 */

/** Oszlopszélességek a megbízás 6.2 pontjából, a tábla szélességének %-ában. */
const OSZLOP_SZELESSEGEK = [
  '3.5', // #
  '3.7', // Kanna vissza
  '3.7', // Kanna ki
  '24.8', // Étterem
  '3.7', // MO
  '3.6', // FO
  '3.6', // F+
  '6.8', // Mennyiség
  '6.9', // Ár
  '13.8', // Összeg
  '5.1', // Tartozás / Átutalás
  '6.0', // Megjegyzés
  '14.6', // Cím
];

/**
 * A sorok egyenletes elosztása a lapok között. (javitasok-jarattervezes.md 2.)
 *
 *   lapok     = felfelé kerekítve (sorok / max)
 *   laponként = felfelé kerekítve (sorok / lapok)
 *
 * 42 cím → 21 + 21, nem 41 + 1. A rövidebb lapot üres, sorszámozott sorok
 * töltik ki — a sofőr ide írja fel kézzel a közben bejött címet. Egyetlen
 * lapnál a lap teljes (41 sor). Ugyanez a szabály a szerveren az Excel
 * exportnál (szerver/src/export/lapbeosztas.ts).
 */
function lapBeosztas<T>(sorok: readonly T[], max: number): { oldalak: T[][]; laponkent: number } {
  const lapok = Math.max(1, Math.ceil(sorok.length / max));
  const laponkent = lapok === 1 ? max : Math.ceil(sorok.length / lapok);
  const oldalak: T[][] = [];
  for (let i = 0; i < lapok; i++) oldalak.push(sorok.slice(i * laponkent, (i + 1) * laponkent));
  return { oldalak, laponkent };
}

export function Nyomtatas({ kezdoNap }: { kezdoNap: string | null }) {
  const alapadatok = useAlapadatok();
  const [napok, setNapok] = useState<NapAdat[]>([]);
  const [datum, setDatum] = useState<string | null>(kezdoNap);
  const [jarat, setJarat] = useState<JaratNap | null>(null);
  const [soforok, setSoforok] = useState<Sofor[]>([]);
  const [hiba, setHiba] = useState<string | null>(null);
  /**
   * Egy lapon, vagy sofőrönként külön? (2.0 / 5.2)
   *
   * Sofőrönként minden sofőr **saját lapot és saját elszámoló oldalt** kap,
   * a nevével a fejlécben. A beosztatlan címek külön lapra kerülnek — így
   * nem vesznek el, de nem is kerülnek véletlenül valakihez.
   */
  const [nyomtatasiMod, setNyomtatasiMod] = useState<'egy' | 'soforonkent'>('egy');

  const oldalSorok = Number(alapadatok.beallitasok.oldal_sorok ?? '41') || 41;

  useEffect(() => {
    api
      .get<NapokValasz>('/api/jarat/napok')
      .then((v) => {
        setNapok(v.napok);
        setDatum((d) => d ?? v.napok.find((n) => n.cimek > 0)?.datum ?? v.napok[0]?.datum ?? null);
      })
      .catch((h: Error) => setHiba(h.message));

    api
      .get<{ sorok: Sofor[] }>('/api/sofor')
      .then((v) => setSoforok(v.sorok.filter((s) => s.aktiv)))
      .catch(() => setSoforok([]));
  }, []);

  const betolt = useCallback(async () => {
    if (!datum) return;
    try {
      setJarat(await api.get<JaratNap>(`/api/jarat/${datum}`));
      setHiba(null);
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'Nem sikerült betölteni a napot.');
    }
  }, [datum]);

  useEffect(() => {
    void betolt();
  }, [betolt]);

  /**
   * A nyomtatandó csoportok. Egy lapon: egyetlen csoport az összes sorral.
   * Sofőrönként: sofőrönként egy, plusz a beosztatlanoké.
   */
  const csoportok = useMemo(() => {
    const sorok = jarat?.sorok ?? [];
    /*
     * Túrasorrend. (A megrendelő kérése, 2026-10-22.)
     *
     * Ha a sofőrnél van megadva útvonal-sorrend, a csoport sorai aszerint
     * mennek; ahol nincs, ott marad a nap sorrendje. A szám nélküli címek
     * a csoport végére kerülnek — így a félig kitöltött sorrend sem
     * borítja fel a lapot.
     */
    const turaszerint = (lista: typeof sorok) => {
      if (!lista.some((x) => x.tura_sorrend != null)) return lista;
      return [...lista].sort(
        (a, b) => (a.tura_sorrend ?? 9999) - (b.tura_sorrend ?? 9999) || a.sorrend - b.sorrend,
      );
    };
    /** Van-e bárhol túrasorrend — az egy lapos mód ettől csoportosít. */
    const vanTura = sorok.some((x) => x.tura_sorrend != null);

    if (nyomtatasiMod === 'egy' && !vanTura) {
      return [{ sofor_id: null as number | null, nev: '', sorok }];
    }

    const lista: Array<{ sofor_id: number | null; nev: string; sorok: typeof sorok }> = [];
    for (const so of soforok) {
      const sajat = sorok.filter((x) => x.sofor_id === so.id);
      if (sajat.length > 0) lista.push({ sofor_id: so.id, nev: so.nev, sorok: turaszerint(sajat) });
    }
    const beosztatlan = sorok.filter((x) => x.sofor_id === null);
    if (beosztatlan.length > 0) {
      lista.push({ sofor_id: null, nev: 'Beosztatlan címek', sorok: turaszerint(beosztatlan) });
    }
    // Ha egyetlen sor sincs kiosztva, ne adjunk üres nyomtatványt.
    if (lista.length === 0) lista.push({ sofor_id: null, nev: '', sorok });
    return lista;
  }, [jarat?.sorok, nyomtatasiMod, soforok]);

  /** Csoportonként az oldalakra bontott sorok — egyenletes elosztással. */
  const csoportOldalak = useMemo(
    () => csoportok.map((cs) => ({ ...cs, ...lapBeosztas(cs.sorok, oldalSorok) })),
    [csoportok, oldalSorok],
  );

  /**
   * Az egyéb címek soronként. Ezek a napon állnak szabad szövegként —
   * útközbeni bolt, benzinkút, technikai cím —, és az utolsó címlap üres
   * soraiba kerülnek. (2026-09-28)
   */
  const egyebCimek = (jarat?.nap.egyeb_cimek ?? '')
    .split(/\r?\n/)
    .map((x) => x.trim())
    .filter(Boolean);

  // Minden csoport a saját címoldalait kapja + egy elszámoló lapot.
  const osszesOldal = csoportOldalak.reduce((n, cs) => n + cs.oldalak.length + 1, 0);

  if (hiba) return <div className="uzenet bad">{hiba}</div>;

  const lapok = (elotag: string, osztaly: 'ppage' | 'psheet') =>
    csoportOldalak.map((cs, csi) => (
      <div key={`${elotag}cs-${cs.sofor_id ?? 'x'}-${csi}`}>
        {cs.oldalak.map((sorok, i) => (
          <div className={osztaly} key={`${elotag}-${csi}-${i}`}>
            <CimOldal
              sorok={sorok}
              oldalIndex={i}
              osszesOldal={cs.oldalak.length + 1}
              tobbOldalas={cs.oldalak.length > 1}
              laponkent={cs.laponkent}
              felirat={jarat?.nap.felirat ?? ''}
              soforNev={cs.nev}
              egyebek={i === cs.oldalak.length - 1 ? egyebCimek : []}
            />
          </div>
        ))}
        <div className={osztaly}>
          <ElszamoloOldal
            jarat={jarat}
            soforok={cs.sofor_id === null ? soforok : soforok.filter((x) => x.id === cs.sofor_id)}
            osszesOldal={cs.oldalak.length + 1}
            felirat={jarat?.nap.felirat ?? ''}
            sorok={cs.sorok}
            soforNev={cs.nev}
          />
        </div>
      </div>
    ));

  return (
    <>
      <div className="dayhead">
        <span style={{ fontSize: 15, fontWeight: 600 }}>Nyomtatási kép</span>
        <select value={datum ?? ''} onChange={(e) => setDatum(e.target.value)}>
          {napok.map((n) => (
            <option key={n.datum} value={n.datum}>
              {n.felirat}
              {n.cimek > 0 ? ` · ${n.cimek} cím` : ''}
            </option>
          ))}
        </select>
        <span className="hint">
          Álló A4, oldalanként legfeljebb {oldalSorok} sor, egyenletesen elosztva. Az utolsó
          oldal az elszámoló lap.
        </span>
        <div className="stats">
          <div>
            Oldal<strong>{osszesOldal}</strong>
          </div>
        </div>
      </div>

      <div className="actbar">
        <button className="btn pri" onClick={() => window.print()}>
          Nyomtatás / PDF
        </button>
        <span style={{ width: 10 }}></span>
        <button
          className={`btn sm${nyomtatasiMod === 'egy' ? ' pri' : ''}`}
          onClick={() => setNyomtatasiMod('egy')}
        >
          Egy lapon
        </button>
        <button
          className={`btn sm${nyomtatasiMod === 'soforonkent' ? ' pri' : ''}`}
          onClick={() => setNyomtatasiMod('soforonkent')}
          title="Minden sofőr saját lapot és saját elszámoló oldalt kap."
        >
          Sofőrönként
        </button>
        <span className="hint">
          A böngésző nyomtatási ablakában: margó „Alapértelmezett", méretezés 100%, a fejléc
          és a lábléc kikapcsolva. Fájlba a „Mentés PDF-ként" lehetőséggel.
        </span>
      </div>

      {/* Képernyős előnézet */}
      <div id="pv">{lapok('pv', 'ppage')}</div>

      {/* Ez csak nyomtatáskor látszik — a papírra kerülő változat. */}
      <div id="printarea">{lapok('pr', 'psheet')}</div>
    </>
  );
}

/* ------------------------------------------------------------------ */

function CimOldal({
  sorok,
  oldalIndex,
  osszesOldal,
  tobbOldalas,
  laponkent,
  felirat,
  soforNev,
  egyebek = [],
}: {
  sorok: JaratNap['sorok'];
  oldalIndex: number;
  osszesOldal: number;
  tobbOldalas: boolean;
  /** Hány sor van egy lapon — a rövidebb lapot üres sorok töltik ki eddig. */
  laponkent: number;
  felirat: string;
  /** Sofőrönkénti nyomtatásnál a fejlécbe kerülő név. (2.0) */
  soforNev?: string;
  /**
   * Egyéb címek: útközbeni bolt, benzinkút — ami nem partner. Az utolsó
   * címlap üres soraiba kerülnek, hogy a sofőrnek ne kézzel kelljen
   * ráírnia. (A megrendelő kérése, 2026-09-28.)
   */
  egyebek?: string[];
}) {
  const elso = oldalIndex * laponkent;
  const uresek = Math.max(0, laponkent - sorok.length - egyebek.length);

  return (
    <>
      <div className="chead">
        <div className="ct">{felirat}</div>
        <div className="cn">
          {/* Sofőrönkénti nyomtatásnál a név ki van töltve, üres vonal helyett. */}
          <span className="nl">{soforNev ? <b>{soforNev}</b> : null}</span>
          <em>Név</em>
        </div>
        {tobbOldalas && (
          <div className="cpg">
            {oldalIndex + 1} / {osszesOldal}
          </div>
        )}
      </div>

      <table className="cl">
        <colgroup>
          {OSZLOP_SZELESSEGEK.map((w, i) => (
            <col key={i} style={{ width: `${w}%` }} />
          ))}
        </colgroup>
        <thead>
          <tr>
            <th></th>
            <th className="sm">
              Kanna
              <br />
              vissza
              <br />
              (db)
            </th>
            <th className="sm">
              Kanna
              <br />
              ki
              <br />
              (db)
            </th>
            <th className="cc">Étterem</th>
            <th className="cc">MO</th>
            <th className="cc">FO</th>
            <th className="cc fpl">F+</th>
            <th className="cc">Mennyiség</th>
            <th className="cc">Ár</th>
            <th className="cc">Összeg</th>
            <th className="sm">
              Tartozás
              <br />
              Átutalás
            </th>
            <th className="sm">Megjegyzés</th>
            <th className="cc">Cím</th>
          </tr>
        </thead>
        <tbody>
          {sorok.map((s, i) => (
            <tr key={s.id}>
              <td className="ix">{elso + i + 1}</td>
              {/* A kannaoszlopokat a sofőr tölti ki kézzel. */}
              <td></td>
              <td></td>
              <td className="et">
                {/*
                  * Állandó cím: csillag a név előtt, hogy a sofőr egy
                  * pillantással lássa, melyik a heti állandó megállója.
                  * (2026-10-08, a megrendelő kérése)
                  */}
                {s.allando && <span className="alj" title="állandó cím">★</span>}
                {s.etterem_nev}
                {/* Egyedi moslékár a sablon szerint: „Aranybástya (4 420)". (feluleti-javitasok.md 1.) */}
                {s.mo_egyedi ? ` (${ft(s.mo_egyedi)})` : ''}
                {/* Kihelyezendő hordó az étteremnév után (javitasok-2.0-teljes.md 3.). */}
                {(s.terv_hordo ?? 0) > 0 && (
                  <span className="hoj">+{szam(s.terv_hordo ?? 0, 0)} HO</span>
                )}
              </td>
              <td className="qq">{s.terv_mo || ''}</td>
              <td className="qq">{s.terv_fo || ''}</td>
              {/* Az F+ jelölő: X, ha F+ olaj — a mennyiség a Mennyiség oszlopban áll. */}
              <td className="qq fpl">{s.fplusz ? 'X' : ''}</td>
              <td className="mn">{s.terv_liter_x ? 'x' : s.terv_liter || ''}</td>
              <td className="ar2">{s.egysegar ? szam(s.egysegar, 0) : ''}</td>
              {/* Az Összeg oszlopot a sofőr írja a papírra. */}
              <td></td>
              {/* A készpénz az alapeset, azt nem írjuk ki — csak a kivételeket. */}
              <td className="qq">{s.fizetesi_mod && s.fizetesi_mod !== 'KP' ? s.fizetesi_mod : ''}</td>
              <td className="mg">{s.megjegyzes ?? ''}</td>
              <td className="cm">
                {[s.etterem_irsz, s.etterem_cim].filter(Boolean).join(' ')}
              </td>
            </tr>
          ))}
          {/* Egyéb címek: a szövegük kinyomtatva, a többi oszlop üres. */}
          {egyebek.map((sz, i) => (
            <tr key={`egyeb-${i}`}>
              <td className="ix">{elso + sorok.length + i + 1}</td>
              <td></td>
              <td></td>
              <td className="et">{sz}</td>
              {Array.from({ length: 9 }, (_, k) => (
                <td key={k} className={k === 2 ? 'fpl' : undefined}></td>
              ))}
            </tr>
          ))}
          {/* Üres, sorszámozott sorok: ide írja a sofőr a közben bejött címet. */}
          {Array.from({ length: uresek }, (_, i) => (
            <tr key={`ures-${i}`}>
              <td className="ix">{elso + sorok.length + egyebek.length + i + 1}</td>
              {Array.from({ length: 12 }, (_, k) => (
                <td key={k} className={k === 5 ? 'fpl' : undefined}></td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

/* ------------------------------------------------------------------ */

/** Üres, kézzel kitöltendő vonalak, a margón betűjelekkel. */
function Vonalak({ darab, jelek = {} }: { darab: number; jelek?: Record<number, string> }) {
  return (
    <table className="bl">
      <tbody>
        {Array.from({ length: darab }, (_, i) => (
          <tr key={i}>
            <td className="gl">{jelek[i] ?? ''}</td>
            <td className="ln"></td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ElszamoloOldal({
  jarat,
  soforok,
  osszesOldal,
  felirat,
  sorok,
  soforNev,
}: {
  jarat: JaratNap | null;
  soforok: Sofor[];
  osszesOldal: number;
  felirat: string;
  /** A lapon szereplő címek — sofőrönkénti nyomtatásnál csak az övéi. (2.0) */
  sorok: JaratNap['sorok'];
  soforNev?: string;
}) {
  /**
   * A fejszámok mindig a lapon szereplő címekből jönnek — sofőrönkénti
   * nyomtatásnál így mindenki a saját MO-, FO-, F+- és literszámát kapja.
   * Az F+ a címek száma: az F+ jelölő, nem mennyiség.
   */
  const o = {
    mo: sorok.reduce((a, x) => a + (x.terv_mo ?? 0), 0),
    fo: sorok.reduce((a, x) => a + (x.terv_fo ?? 0), 0),
    fplusz: sorok.filter((x) => x.fplusz).length,
    liter: sorok.reduce((a, x) => a + (x.terv_liter ?? 0), 0),
  };
  // A sofőr neve a lap tetején — sofőrönkénti nyomtatásnál.
  const fejlecNev = soforNev ? `${felirat} — ${soforNev}` : felirat;

  // A régi sablonon "2026." állt — ez az évszám mezője. A nap dátumából
  // vesszük, hogy jövőre ne kelljen hozzányúlni.
  const ev = jarat?.nap.datum?.slice(0, 4) ?? String(new Date().getFullYear());

  // Legalább négy sor kell az autókészlet táblába, hogy legyen hova írni.
  const autoSorok = [
    ...soforok.map((s) => s.nev.toUpperCase()),
    ...Array.from({ length: Math.max(1, 4 - soforok.length) }, () => ''),
  ];

  const akSor = (nev: string, kulcs: string) => (
    <tr key={kulcs}>
      <td className="dn">{nev || ' '}</td>
      {Array.from({ length: 6 }, (_, i) => (
        <td className="ac" key={i}></td>
      ))}
    </tr>
  );

  return (
    <>
      <div className="phead">
        <h2>Elszámolás</h2>
        <span>{soforNev ? fejlecNev : `${felirat} · sofőr: ________________`}</span>
        <span className="pg">
          {osszesOldal} / {osszesOldal}
        </span>
      </div>

      {/* A nap tervezett összesítése — a lap tetején, ahogy a régi sablonon. */}
      <div className="tot4">
        <span>{szam(o.mo, 0)}</span>
        <span>{szam(o.fo, 0)}</span>
        <span>{szam(o.fplusz, 0)}</span>
        <span>{szam(o.liter, 0)}</span>
      </div>

      <div className="p2">
        {/* --- bal oldal: BESZEDÉS és KIADÁS --- */}
        <div>
          <div className="hd2">BESZEDÉS:</div>
          <Vonalak darab={16} jelek={{ 0: 'K', 5: 'MO', 10: 'H' }} />
          <div className="hd2" style={{ marginTop: 14 }}>
            KIADÁS:
          </div>
          <Vonalak darab={6} jelek={{ 0: 'K', 3: 'H' }} />
        </div>

        {/* --- jobb oldal --- */}
        <div className="rblk">
          <div className="r2c">
            <div>
              <table className="bl">
                <tbody>
                  <tr>
                    <td className="k3">LITER:</td>
                    <td className="ln"></td>
                  </tr>
                </tbody>
              </table>
              <Vonalak darab={5} />
            </div>
            <div>
              <table className="bl sp2">
                <tbody>
                  {['ÖSSZEG:', 'ÁFA:', 'UTALÁS:', 'LEADÓ:'].map((cimke) => (
                    <tr key={cimke}>
                      <td className="k4">{cimke}</td>
                      <td className="ln"></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="aktitle">AUTÓKÉSZLET</div>
          <div className="akbox">
            <table className="akt2">
              <tbody>
                <tr>
                  <th>NÉV</th>
                  <th>LEADÓ</th>
                  <th>CÍM</th>
                  <th>LITER</th>
                  <th>NYITÓ</th>
                  <th>FV</th>
                  <th>ZÁRÓ</th>
                </tr>
                {autoSorok.map((nev, i) => akSor(nev, `ak-${i}`))}
              </tbody>
            </table>
          </div>

          <div className="r2c" style={{ marginTop: 14 }}>
            <div>
              <table className="bl">
                <tbody>
                  <tr>
                    <td className="k3">HSO:</td>
                    <td className="ln"></td>
                  </tr>
                </tbody>
              </table>
              <Vonalak darab={2} />
            </div>
            <div>
              <table className="bl">
                <tbody>
                  {[`${ev}.`, 'F+:', 'R:', 'Á:'].map((cimke) => (
                    <tr key={cimke}>
                      <td className="k5">{cimke}</td>
                      <td className="ln"></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
